/**
 * Product-card FOMO line rotator.
 *
 * Binds to every `.pcard__fomo[data-fomo-strip]` on the page:
 *  - 0 pills    → element renders nothing (Liquid bails early)
 *  - 1 pill     → permanently active, no rotation
 *  - 2+ pills   → crossfade through them every ~3s, with a small
 *                 per-card offset so the grid doesn't blink in
 *                 sync.
 *
 * Pauses when the page is hidden (`visibilitychange`) to save
 * CPU/battery, and resumes on return. Also pauses during
 * IntersectionObserver-detected off-screen state for the same
 * reason - a card halfway down a 30-item grid doesn't need to
 * be ticking timers.
 *
 * Re-scans on DOM mutations (e.g. AJAX-loaded predictive search
 * results or section-rendering swaps) so newly-injected cards
 * pick up the rotation automatically.
 *
 * Design source: claude.ai/design "Product Cards v4 - FOMO line
 * between title & price" (chat2).
 */

(function () {
  "use strict";

  /* Run-once guard. Historically this file was emitted as a <script> tag
     by every card-product render (108× on the homepage). Each tag re-runs
     the file - same-src scripts execute per element - so every execution
     created its own MutationObserver on document.body, its own
     IntersectionObserver, its own registry and its own rotation timers,
     multiplying forced reflows by the number of cards on the page. The
     tag is now emitted once from theme.liquid, and this guard makes any
     stray duplicate (old cached fragments, app-injected cards) a no-op. */
  if (window.__pcardFomoInit) return;
  window.__pcardFomoInit = true;

  var INTERVAL_MS = 5000; // 5s per pill - premium pacing, longer dwell
  var ROOTMARGIN = "200px 0px 200px 0px"; // start rotating before the card enters view

  var registry = new WeakMap(); // strip element -> { pills, idx, timer, visible }
  var visibilityObserver = null;
  var reducedMotion = false;

  /* Default "Arrives tomorrow" banner is time-aware: before the 9pm Dubai
     (UTC+4) cutoff it promises "Arrives today"; after 9pm it falls back to
     "Arrives tomorrow". Done in JS (not Liquid) because storefront pages
     are cached, so a server-rendered time would go stale. */
  var DELIVERY_CUTOFF_HOUR = 21; // 9pm
  function updateDeliveryEta(root) {
    var now = new Date();
    var dubai = new Date(now.getTime() + now.getTimezoneOffset() * 60000 + 4 * 3600000);
    // Full phrase on every screen size (the banner is sized to fit it - see
    // .pcard__fomo--banner rules in pcard-fomo.css).
    (root || document).querySelectorAll("[data-delivery-eta]").forEach(function (el) {
      var banner = el.closest("[data-delivery-today][data-delivery-tomorrow]");
      if (!banner) return;
      var text = dubai.getHours() < DELIVERY_CUTOFF_HOUR
        ? banner.dataset.deliveryToday
        : banner.dataset.deliveryTomorrow;
      if (el.textContent !== text) el.textContent = text;
    });
  }

  function init() {
    if (window.matchMedia) {
      var mq = window.matchMedia("(prefers-reduced-motion: reduce)");
      reducedMotion = mq.matches;
      // If the user changes the OS-level preference, sync without reload
      var onChange = function () { reducedMotion = mq.matches; };
      if (mq.addEventListener) mq.addEventListener("change", onChange);
      else if (mq.addListener) mq.addListener(onChange);
    }

    if ("IntersectionObserver" in window) {
      visibilityObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var entry_state = registry.get(entry.target);
          if (!entry_state) return;
          entry_state.visible = entry.isIntersecting;
          if (entry.isIntersecting) scheduleNext(entry.target, entry_state);
          else clearTimer(entry_state);
        });
      }, { rootMargin: ROOTMARGIN });
    }

    bindAll();
    updateDeliveryEta();
    // Re-check the cutoff each minute so a page left open across 9pm flips.
    setInterval(updateDeliveryEta, 60000);
    // Crossing the mobile breakpoint (rotate / resize) swaps short vs full label.
    var resizeTimer = null;
    window.addEventListener("resize", function () {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () { updateDeliveryEta(); }, 150);
    });

    // Re-scan when new cards arrive (AJAX collection load, search,
    // section render API swap, infinite scroll, etc.).
    var mo = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches("[data-fomo-strip]")) bind(node);
          if (node.querySelectorAll) {
            node.querySelectorAll("[data-fomo-strip]").forEach(bind);
          }
          // Freshly-injected cards get the correct delivery ETA text.
          if (node.querySelector && node.querySelector("[data-delivery-eta]")) {
            updateDeliveryEta(node);
          }
        }
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });

    document.addEventListener("visibilitychange", function () {
      if (document.hidden) {
        // Pause all
        registry.forEach && registry.forEach(function (state) { clearTimer(state); });
        // WeakMap has no forEach - iterate via a sentinel set instead.
        document.querySelectorAll("[data-fomo-strip]").forEach(function (el) {
          var s = registry.get(el);
          if (s) clearTimer(s);
        });
      } else {
        document.querySelectorAll("[data-fomo-strip]").forEach(function (el) {
          var s = registry.get(el);
          if (s && s.visible !== false) scheduleNext(el, s);
        });
      }
    });
  }

  function bindAll() {
    document.querySelectorAll("[data-fomo-strip]").forEach(bind);
  }

  function bind(strip) {
    if (registry.has(strip)) return;

    var pills = Array.prototype.slice.call(
      strip.querySelectorAll(".pcard__fomo-pill")
    );
    if (pills.length === 0) return;

    var state = {
      pills: pills,
      idx: 0,
      timer: null,
      visible: true,
    };
    registry.set(strip, state);

    pills[0].classList.add("is-active");

    if (pills.length < 2) return; // single tag → no rotation needed

    if (visibilityObserver) visibilityObserver.observe(strip);

    var offset = parseInt(strip.getAttribute("data-offset") || "0", 10);
    // Stagger first tick by per-card offset so adjacent cards
    // don't switch in lockstep across a grid.
    state.timer = setTimeout(function () {
      tick(strip, state);
    }, INTERVAL_MS + offset);
  }

  function scheduleNext(strip, state) {
    if (state.pills.length < 2) return;
    if (reducedMotion) return; // user preference - keep first pill static
    clearTimer(state);
    state.timer = setTimeout(function () {
      tick(strip, state);
    }, INTERVAL_MS);
  }

  function tick(strip, state) {
    state.idx = (state.idx + 1) % state.pills.length;
    showPill(state, state.idx);
    if (state.visible !== false) {
      state.timer = setTimeout(function () {
        tick(strip, state);
      }, INTERVAL_MS);
    }
  }

  function showPill(state, idx) {
    var nextPill = state.pills[idx];
    if (!nextPill) return;
    if (nextPill.classList.contains("is-active")) return;
    nextPill.classList.add("is-entering");
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        if (!nextPill.isConnected) return;
        for (var i = 0; i < state.pills.length; i++) {
          state.pills[i].classList.toggle("is-active", i === idx);
        }
        nextPill.classList.remove("is-entering");
      });
    });
  }

  function clearTimer(state) {
    if (state.timer) {
      clearTimeout(state.timer);
      state.timer = null;
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
